import { taxonomyTagMatch } from './catalog-taxonomy';
import { HOME_TAG_SLUGS } from './home-tags';

/**
 * What a product's deliverables say it is: the files in its pool (their
 * extensions, MIME types and names) and its access links.
 *
 * ONLY HOME_TAGS SLUGS. Every slug a rule names is one of HOME_TAGS
 * (home-tags.ts), a fixed list spelled as the tags it stands for, so a
 * suggestion joins an existing tag rather than minting "template" beside
 * "templates". tag-suggestions.test.ts fails on any rule that names a slug
 * outside that list.
 *
 * TWO READERS. Most of those slugs ("templates", "fonts", "presets") name a
 * catalogue type now (catalog-taxonomy.ts), so the listing forms guess the
 * product's type from rankedDeliverableHints, and the tag picker is offered
 * only the rest (suggestTagsForProduct): the tools and sub-kinds, which stay
 * tags.
 *
 * TWO STRENGTHS. A STRONG hint is a file type that means one thing (a .ttf is a
 * font, a .unitypackage is a game asset) or a word in a file name or link that
 * says what the product is ("mockup", "planner"). A WEAK hint is a type shared
 * by several kinds of product: a PDF is as often a planner as an ebook, and a
 * PNG could be anything. Weak hints are offered only when nothing strong turned
 * up, so a font family with a PDF specimen suggests "fonts" and not "ebooks".
 *
 * ARCHIVES SAY NOTHING. A .zip names no type, so a zipped product is suggested
 * for only by the words in its file name. Reading inside archives would take
 * an unzip in the browser for a hint; this keeps to what the form already has.
 *
 * PURE: no React, no Firestore. The tag picker renders the result (see
 * TagSelector's `suggestedTags`) and both product forms feed it.
 */

/** A deliverable as the forms hold it: a picked File, or a stored pool entry. */
export type DeliverableFile = {
  name: string;
  /** The browser's MIME type, when known. Stored pool entries have none. */
  type?: string | null;
};

export type TagSuggestionInput = {
  files?: ReadonlyArray<DeliverableFile>;
  /** Access links (`details.accessLinks.links`), as typed. */
  links?: ReadonlyArray<string>;
};

/** How many suggestions the picker shows, at most. */
export const MAX_TAG_SUGGESTIONS = 6;

const STRONG = 2;
const WEAK = 1;
type Strength = typeof STRONG | typeof WEAK;
type Hint = { slug: string; strength: Strength };

const strong = (...slugs: string[]): Hint[] => slugs.map((slug) => ({ slug, strength: STRONG }));
const weak = (...slugs: string[]): Hint[] => slugs.map((slug) => ({ slug, strength: WEAK }));

/**
 * File extensions, lowercased, to what they suggest. Space-separated so each
 * row reads as one decision. The first row says nothing on purpose, and is
 * listed so that the file's MIME type cannot speak for it either: an archive
 * could hold anything, `ts` is TypeScript as often as an MPEG video segment
 * (Chrome types it video/mp2t), and `ase` is both an Aseprite sprite and an
 * Adobe colour swatch.
 */
const EXTENSION_RULES: ReadonlyArray<readonly [string, Hint[]]> = [
  ['zip rar 7z tar gz tgz bz2 xz ts ase', []],
  ['ttf otf woff woff2 eot ttc', strong('fonts')],
  ['abr brushset brush sut kpp', strong('brushes')],
  ['xmp lrtemplate costyle costylepack cube 3dl atn prfpset ffx fxp fxb vital', strong('presets')],
  ['dng', weak('presets')],
  ['obj fbx stl blend gltf glb dae 3ds max c4d usdz usd ply 3mf ma mb skp', strong('3d-models')],
  ['fig sketch xd', weak('ui-kits', 'templates')],
  ['pptx ppt potx key dotx xltx indt mogrt motn', strong('templates')],
  ['xlsx xls numbers indd idml aep prproj drp', weak('templates')],
  ['docx doc pages odt rtf', weak('templates', 'ebooks')],
  ['epub mobi azw azw3', strong('ebooks')],
  ['pdf', weak('ebooks', 'printables', 'planners')],
  ['goodnotes', strong('planners')],
  ['ico icns', strong('icons')],
  ['svg', weak('graphics', 'icons')],
  ['ai eps psd png jpg jpeg webp gif tif tiff bmp heic afdesign afphoto cdr procreate', weak('graphics')],
  ['js mjs cjs jsx tsx py rb php java kt go rs c cc cpp h hpp cs swift dart vue svelte sh ipynb', strong('code')],
  ['html css scss sql lua', weak('code')],
  ['8bf aex aip zxp ccx vst3 crx xpi sketchplugin', strong('plugins')],
  ['mid midi als flp logicx ptx', strong('music')],
  ['wav mp3 flac aiff aif ogg m4a opus wma', weak('music', 'sound-effects')],
  ['mp4 mov m4v webm mkv avi', weak('courses')],
  ['unitypackage uasset umap tscn tres tmx rbxm rbxl mcaddon mcpack mcworld yyp', strong('game-assets')],
  ['aseprite', weak('game-assets', 'graphics')],
];

/**
 * Words in a file name or link path, to the tag they name. Each entry is one
 * word, or a phrase of words that must appear in a row ("wall art"). A single
 * word also matches its plural and a bundle word fused onto it ("iconpack",
 * "brushset", "spritesheet"); see wordMatches. Every word here is one that is
 * rarely anything else in a product's file name: "background" is left out
 * because "background-music.mp3" is not a graphic, "stem" because STEM
 * worksheets are not stems, "add on" because a planner add-on is not a plugin.
 */
const KEYWORD_RULES: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['templates', ['template']],
  ['notion', ['notion']],
  ['canva', ['canva']],
  ['planners', ['planner', 'journal', 'agenda', 'tracker', 'calendar']],
  ['printables', ['printable', 'worksheet', 'coloring', 'colouring', 'flashcard', 'wallart', 'wall art']],
  ['ebooks', ['ebook', 'e book']],
  ['courses', ['course', 'lesson', 'tutorial', 'masterclass', 'master class', 'workshop', 'lecture']],
  ['fonts', ['font', 'typeface']],
  ['icons', ['icon']],
  ['graphics', [
    'graphic', 'clipart', 'clip art', 'illustration', 'vector', 'texture', 'pattern', 'sticker', 'overlay',
    'wallpaper',
  ]],
  ['mockups', ['mockup', 'mock up']],
  ['brushes', ['brush']],
  ['presets', ['preset', 'lut', 'lightroom']],
  ['ui-kits', ['uikit', 'ui kit', 'wireframe', 'design system']],
  ['3d-models', ['3d', 'lowpoly', 'low poly']],
  ['code', ['sourcecode', 'source code', 'snippet', 'boilerplate']],
  ['plugins', ['plugin', 'extension', 'vst']],
  ['music', ['beat', 'instrumental', 'soundtrack', 'song', 'track', 'lofi', 'midi', 'melody']],
  ['sound-effects', ['sfx', 'soundeffect', 'sound effect', 'foley', 'whoosh', 'swoosh', 'ambience']],
  ['game-assets', ['sprite', 'tileset', 'tile set', 'gameasset', 'game asset', 'unity', 'godot', 'unreal']],
];

/**
 * Link hosts to what they suggest, matched on the host or any subdomain of it.
 * A Notion or Canva link is the product itself (a duplicable template), so both
 * of its tags are strong. A Figma link may be a UI kit or a template, a Google
 * Docs link is usually a template, a video link is usually a course.
 */
const LINK_HOST_RULES: ReadonlyArray<readonly [readonly string[], Hint[]]> = [
  [['notion.so', 'notion.site'], strong('notion', 'templates')],
  [['canva.com', 'canva.link'], strong('canva', 'templates')],
  [['github.com', 'gitlab.com', 'bitbucket.org'], strong('code')],
  [['figma.com'], weak('ui-kits', 'templates')],
  [['docs.google.com'], weak('templates')],
  [['youtube.com', 'youtu.be', 'vimeo.com', 'loom.com'], weak('courses')],
];

/**
 * MIME types to what they suggest, for a file whose extension matched no rule:
 * the browser derives most types from the extension anyway, and gets some
 * wrong (Chrome calls a .ts file video/mp2t). A pattern ending in "/" matches
 * the whole family; any other names one type exactly.
 */
const MIME_RULES: ReadonlyArray<readonly [string, Hint[]]> = [
  ['font/', strong('fonts')],
  ['model/', strong('3d-models')],
  ['application/epub+zip', strong('ebooks')],
  ['application/pdf', weak('ebooks', 'printables', 'planners')],
  ['audio/', weak('music', 'sound-effects')],
  ['video/', weak('courses')],
  ['image/', weak('graphics')],
];

const EXTENSION_HINTS: ReadonlyMap<string, Hint[]> = new Map(
  EXTENSION_RULES.flatMap(([exts, hints]) => exts.split(' ').map((ext) => [ext, hints] as const)),
);

const KEYWORD_PHRASES: ReadonlyArray<{ slug: string; words: string[] }> = KEYWORD_RULES.flatMap(
  ([slug, phrases]) => phrases.map((phrase) => ({ slug, words: phrase.split(' ') })),
);

/** Every slug any rule can suggest. Exported for the test that pins them to HOME_TAGS. */
export const SUGGESTABLE_TAGS: readonly string[] = Array.from(new Set([
  ...KEYWORD_RULES.map(([slug]) => slug),
  ...[...EXTENSION_RULES, ...MIME_RULES, ...LINK_HOST_RULES].flatMap(([, hints]) => hints.map((h) => h.slug)),
]));

const PLURALS = ['', 's', 'es'];
const BUNDLE_WORDS = [
  '', 'pack', 'packs', 'set', 'sets', 'kit', 'kits', 'bundle', 'bundles', 'collection', 'collections',
  'library', 'sheet', 'sheets',
];

/**
 * Whether a name token is `word`, its plural, or either with a bundle word
 * fused on: "icon", "icons", "iconpack", "iconsets", "brushes", "spritesheet".
 * Anything else that merely starts with the word does not match, so "iconic"
 * is not an icon and "tracker" is not a music track.
 */
function wordMatches(token: string, word: string): boolean {
  if (!token.startsWith(word)) return false;
  const rest = token.slice(word.length);
  return PLURALS.some((plural) => rest.startsWith(plural) && BUNDLE_WORDS.includes(rest.slice(plural.length)));
}

/**
 * A file name or link path as lowercase words. Splits on anything that is not
 * a letter or digit, and on camelCase ("IconPack", "UIKit", "3DModels"), but
 * never between a digit and a letter, so "3d" survives as one word. A word with
 * a number fused on the end or the start ("planner2026", "2026planner") is also
 * offered without it.
 */
function nameTokens(text: string): string[][] {
  const words = text
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z0-9])([A-Z][a-z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return words.map((word) => {
    const forms = [word, word.replace(/^[0-9]+/, ''), word.replace(/[0-9]+$/, '')];
    return Array.from(new Set(forms.filter(Boolean)));
  });
}

/**
 * The tags the words in a name or path name, strong, in the order their words
 * appear: "Notion Budget Template" suggests notion before templates.
 */
function keywordHints(text: string): Hint[] {
  const tokens = nameTokens(text);
  const firstAt = new Map<string, number>();
  for (const { slug, words } of KEYWORD_PHRASES) {
    const start = tokens.findIndex((_, at) => words.every((word, offset) => {
      const forms = tokens[at + offset];
      if (!forms) return false;
      // Only a phrase's LAST word may be plural or bundled ("sound effects",
      // "ui kits"); the words before it must be exact.
      return offset === words.length - 1
        ? forms.some((form) => wordMatches(form, word))
        : forms.includes(word);
    }));
    if (start >= 0 && start < (firstAt.get(slug) ?? Infinity)) firstAt.set(slug, start);
  }
  return Array.from(firstAt)
    .sort((a, b) => a[1] - b[1])
    .map(([slug]): Hint => ({ slug, strength: STRONG }));
}

/** What a MIME type alone suggests (see MIME_RULES), ignoring any parameters. */
function mimeHints(type: string): Hint[] {
  const mime = type.toLowerCase().split(';')[0].trim();
  const rule = MIME_RULES.find(([pattern]) => (pattern.endsWith('/') ? mime.startsWith(pattern) : mime === pattern));
  return rule ? rule[1] : [];
}

function fileHints(file: DeliverableFile): Hint[] {
  const name = typeof file.name === 'string' ? file.name.trim() : '';
  const dot = name.lastIndexOf('.');
  // A leading dot (".gitkeep") is a hidden file's whole name, not an extension.
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
  const base = dot > 0 ? name.slice(0, dot) : name;
  const byType = EXTENSION_HINTS.get(ext) ?? (typeof file.type === 'string' ? mimeHints(file.type) : []);
  return [...keywordHints(base), ...byType];
}

function parseLink(raw: string): URL | null {
  const text = raw.trim();
  if (!text) return null;
  for (const candidate of [text, `https://${text}`]) {
    try {
      const url = new URL(candidate);
      if ((url.protocol === 'https:' || url.protocol === 'http:') && url.hostname.includes('.')) return url;
    } catch {
      // Not a URL in this form; try the next.
    }
  }
  return null;
}

function linkHints(raw: string): Hint[] {
  const url = parseLink(raw);
  if (!url) return [];
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const byHost = LINK_HOST_RULES
    .filter(([hosts]) => hosts.some((h) => host === h || host.endsWith(`.${h}`)))
    .flatMap(([, hints]) => hints);
  let path = url.pathname;
  try {
    path = decodeURIComponent(path);
  } catch {
    // A malformed escape: read the path as it is.
  }
  // The host first: a notion.so link is a Notion product whatever its page is
  // called, so its tags lead the words in the path.
  return [...byHost, ...keywordHints(path)];
}

/** One thing the deliverables point to, with how sure the evidence is. */
export type DeliverableHint = { slug: string; strong: boolean; sources: number };

/**
 * Everything the deliverables point to, best first: by strength, then by how
 * many files and links back it, then by the order the evidence arrived in (the
 * forms list deliverables in the order the buyer gets them, so the first file
 * is the one the product is about). Weak hints are kept; callers decide
 * whether a strong one drowns them. The type guess
 * (catalog-taxonomy-inference.ts) weighs both.
 */
export function rankedDeliverableHints(input: TagSuggestionInput): DeliverableHint[] {
  const tally = new Map<string, { strength: Strength; sources: number }>();
  const sources = [
    ...(input.files ?? []).map(fileHints),
    ...(input.links ?? []).map((link) => (typeof link === 'string' ? linkHints(link) : [])),
  ];
  for (const hints of sources) {
    // One file or link backs a tag once, at the strongest it suggested it.
    const best = new Map<string, Strength>();
    for (const { slug, strength } of hints) {
      if ((best.get(slug) ?? 0) < strength) best.set(slug, strength);
    }
    for (const [slug, strength] of best) {
      const entry = tally.get(slug);
      if (entry) {
        entry.strength = entry.strength > strength ? entry.strength : strength;
        entry.sources += 1;
      } else {
        tally.set(slug, { strength, sources: 1 });
      }
    }
  }
  return Array.from(tally, ([slug, entry]) => ({ slug, ...entry }))
    .filter(({ slug }) => HOME_TAG_SLUGS.includes(slug))
    // Array.prototype.sort is stable, so equal entries keep their arrival order.
    .sort((a, b) => b.strength - a.strength || b.sources - a.sources)
    .map(({ slug, strength, sources }) => ({ slug, strong: strength === STRONG, sources }));
}

/**
 * What the deliverables point to, as HOME_TAGS slugs, best first, at most
 * `limit` of them. When any is strongly suggested, the weak ones are dropped.
 */
export function suggestTagsForDeliverables(
  input: TagSuggestionInput,
  limit: number = MAX_TAG_SUGGESTIONS,
): string[] {
  const ranked = rankedDeliverableHints(input);
  const anyStrong = ranked.some((hint) => hint.strong);
  return ranked
    .filter((hint) => !anyStrong || hint.strong)
    .slice(0, Math.max(0, limit))
    .map((hint) => hint.slug);
}

/**
 * The tags the tag picker suggests: suggestTagsForDeliverables, less the
 * slugs that name a catalogue type or purpose ("templates", "fonts",
 * "music"). Those are fields now (catalog-taxonomy.ts) and the picker refuses
 * them as tags, so what is left is the tools and sub-kinds: notion, canva,
 * icons, sound-effects.
 */
export function suggestTagsForProduct(
  input: TagSuggestionInput,
  limit: number = MAX_TAG_SUGGESTIONS,
): string[] {
  return suggestTagsForDeliverables(input, Infinity)
    .filter((slug) => taxonomyTagMatch(slug) === null)
    .slice(0, Math.max(0, limit));
}
